import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Manages cart terms acceptance (general + gift card).
 * Works after AJAX cart updates via ThemeEvents.cartUpdate + queueMicrotask
 * to re-read state after morphSection completes.
 */
class CartTermsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#updateState();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /** Re-sync after morphSection (which runs synchronously before microtasks). */
  #handleCartUpdate = () => {
    queueMicrotask(() => this.#updateState());
  };

  /** Called by on:change="/onTermsChange" on the general terms checkbox. */
  onTermsChange() {
    const checkbox = this.querySelector('#accept-terms');
    if (checkbox instanceof HTMLInputElement && checkbox.checked) {
      this.#hideError('terms-error');
      this.#saveConsent();
    }
    this.#updateState();
  }

  /** Called by on:change="/onGiftCardTermsChange" on the gift card terms checkbox. */
  onGiftCardTermsChange() {
    const checkbox = this.querySelector('#accept-gift-card-terms');
    if (checkbox instanceof HTMLInputElement && checkbox.checked) {
      this.#hideError('gift-card-terms-error');
    }
    this.#updateState();
  }

  /** Called by on:click="/onCheckoutClick" on the checkout button. */
  onCheckoutClick(event) {
    if (this.#allTermsAccepted()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    let focused = false;

    const termsCheckbox = this.querySelector('#accept-terms');
    if (termsCheckbox instanceof HTMLInputElement && !termsCheckbox.checked) {
      this.#showError('terms-error');
      if (!focused) {
        termsCheckbox.focus();
        focused = true;
      }
    }

    const giftCardCheckbox = this.querySelector('#accept-gift-card-terms');
    if (giftCardCheckbox instanceof HTMLInputElement && !giftCardCheckbox.checked) {
      this.#showError('gift-card-terms-error');
      if (!focused) {
        giftCardCheckbox.focus();
      }
    }
  }

  #allTermsAccepted() {
    const termsCheckbox = this.querySelector('#accept-terms');
    const giftCardCheckbox = this.querySelector('#accept-gift-card-terms');
    return (
      (!(termsCheckbox instanceof HTMLInputElement) || termsCheckbox.checked) &&
      (!(giftCardCheckbox instanceof HTMLInputElement) || giftCardCheckbox.checked)
    );
  }

  #updateState() {
    const allOk = this.#allTermsAccepted();
    const btn = this.querySelector('#checkout');

    if (btn instanceof HTMLButtonElement) btn.disabled = !allOk;

    // Controls CSS visibility of accelerated checkout buttons
    this.toggleAttribute('terms-accepted', allOk);
  }

  #showError(id) {
    const el = this.querySelector(`#${id}`);
    if (el instanceof HTMLElement) el.hidden = false;
  }

  #hideError(id) {
    const el = this.querySelector(`#${id}`);
    if (el instanceof HTMLElement) el.hidden = true;
  }

  /** Saves consent timestamp to cart note (only on general terms acceptance). */
  #saveConsent() {
    const note = `Consenso T&C accettato il ${new Date().toLocaleString('it-IT')}`;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    }).catch(console.error);
  }
}

if (!customElements.get('cart-terms-component')) {
  customElements.define('cart-terms-component', CartTermsComponent);
}
